// service that caches responses to local storage with TTL (Time-To-Live) expiration
import { Injectable } from '@angular/core';

export const ONE_HOUR = 3600000; // 1 hour in milliseconds
export const ONE_DAY = 86400000; // 24 hours in milliseconds
export const ONE_WEEK = 604800000; // 7 days in milliseconds

const CACHE_KEY = 'cache';

interface CacheEntry {
    value: unknown;
    expiresAt: number; // Timestamp when entry expires
}

@Injectable({
    providedIn: 'root'
})
export class CacheService {
    private cache: Map<string, CacheEntry> = new Map();

    constructor() {
        this.hydrateFromStorage();
    }

    /**
     * Store a value in cache with optional TTL (Time-To-Live).
     * @param key - Cache key
     * @param value - Value to cache
     * @param ttlMs - Time-to-live in milliseconds (default: 1 hour)
     */
    public setItem(key: string, value: unknown, ttlMs: number = ONE_HOUR): void {
        const expiresAt = Date.now() + ttlMs;
        this.cache.set(key, { value, expiresAt });
        this.persistCache();
    }

    /**
     * Retrieve a value from cache. Returns null if expired or not found.
     * @param key - Cache key
     * @returns Cached value or null
     */
    public getItem(key: string): unknown {
        const entry = this.cache.get(key);

        if (!entry) {
            return null;
        }

        // Check if entry has expired
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key);
            this.persistCache();
            return null;
        }

        return entry.value;
    }

    /**
     * Reset tickets list by clearing its cached value while preserving TTL.
     */
    public resetTicketsList(): void {
        let ticketsListKey: string | undefined;

        this.cache.forEach((entry, key) => {
            const value = entry.value;
            if (value instanceof Array && value[0]?.productId && value[0]?.productCode) {
                ticketsListKey = key;
            }
        });

        if (ticketsListKey) {
            const existingEntry = this.cache.get(ticketsListKey);
            if (existingEntry) {
                // Preserve the TTL when resetting
                this.cache.set(ticketsListKey, { value: [], expiresAt: existingEntry.expiresAt });
                this.persistCache();
            }
        }
    }

    /**
     * Clear entire cache.
     */
    public clearCache(): void {
        this.cache.clear();
        try {
            localStorage.removeItem(CACHE_KEY);
        } catch (error) {
            console.warn('Failed to clear cache from localStorage:', error);
        }
    }

    /**
     * Hydrate cache from localStorage, filtering out expired entries.
     */
    private hydrateFromStorage(): void {
        try {
            const stored = localStorage.getItem(CACHE_KEY);
            if (!stored) {
                return;
            }

            const parsed: [string, CacheEntry][] = JSON.parse(stored);
            const now = Date.now();

            // Filter out expired entries during hydration
            this.cache = new Map(parsed.filter(([, entry]) => entry.expiresAt > now));
        } catch (error) {
            console.warn('Failed to hydrate cache from localStorage:', error);
            this.cache = new Map();
        }
    }

    /**
     * Persist cache to localStorage with quota error handling.
     */
    private persistCache(): void {
        try {
            const serialized = JSON.stringify(Array.from(this.cache.entries()));
            localStorage.setItem(CACHE_KEY, serialized);
        } catch (error) {
            // Handle quota exceeded errors (Chrome and Firefox)
            if (
                error instanceof DOMException &&
                (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
            ) {
                console.warn('localStorage quota exceeded. Attempting to free space...');

                // Step 1: Try to evict expired entries
                this.evictExpiredEntries();

                try {
                    const serialized = JSON.stringify(Array.from(this.cache.entries()));
                    localStorage.setItem(CACHE_KEY, serialized);
                    return;
                } catch (retryError) {
                    // Step 2: If still failing, clear entire cache as fallback
                    console.error('Failed to free space. Clearing cache entirely.');
                    this.clearCache();
                }
            } else {
                console.error('Failed to persist cache:', error);
            }
        }
    }

    /**
     * Remove all expired entries from cache.
     */
    private evictExpiredEntries(): void {
        const now = Date.now();
        const keysToDelete: string[] = [];

        this.cache.forEach((entry, key) => {
            if (entry.expiresAt <= now) {
                keysToDelete.push(key);
            }
        });

        keysToDelete.forEach((key) => this.cache.delete(key));
    }
}
